import SessionContext from '@providers/Session/SessionContext';
import { Select } from 'antd';
import React, {
  CSSProperties,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import useSearchParams from '@hooks/useSearchParams';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import { IChartDataResponse, useBillingChart } from './hooks/useBillingChart';
import { BillingChartCard } from './components/BillingChartCard';

const styles: { [key: string]: CSSProperties } = {
  select: {
    width: '100%',
    maxWidth: '15rem',
  },
};

const BillingDashboard: React.FC = () => {
  const { setSearchParams, clearSearchParams, searchParams } = useSearchParams({
    extraParams: ['lastTimeOpened', 'lastTimeClosed', 'produto'],
  });

  const [formData, setFormData] = useState({
    lastTimeClosed: '365d',
    lastTimeOpened: '7d',
    produto: '',
  });

  const [showFiltersClosed, setShowFiltersClosed] = useState(true);
  const [showFiltersOpened, setShowFiltersOpened] = useState(true);

  const { t, i18n } = useTranslation([
    'date-picker',
    'monthly-billing',
    'consumer-traffic-by-customer',
  ]);

  const currentLanguage = i18n.language;

  const { customersSwitch, selectedApp, theme, graphThemeKey } =
    useContext(SessionContext);

  const buildRequestParams = useCallback(
    (lastTime: string) => ({
      params: {
        lastTime,
        produto:
          formData?.produto ||
          (ApplicationsConstants.FINANCIAL?.toUpperCase() !==
          selectedApp?.toUpperCase()
            ? selectedApp?.toUpperCase()
            : undefined),
      },
      headers: {
        customerId: customersSwitch?.selected,
      },
    }),
    [formData?.produto, customersSwitch?.selected, selectedApp],
  );

  const requestParamsClosed = useMemo(
    () => buildRequestParams(formData?.lastTimeClosed || '365d'),
    [buildRequestParams, formData?.lastTimeClosed],
  );

  const requestParamsOpened = useMemo(
    () => buildRequestParams(formData?.lastTimeOpened || '7d'),
    [buildRequestParams, formData?.lastTimeOpened],
  );

  const { data: closedInvoiceData, isLoading: isLoadingClosedInvoice } = useSwr<
    IChartDataResponse[]
  >(
    '/service-proxy/dashboard/grafico/financeiro/fatura_fechada/historico/produto',
    requestParamsClosed,
  );

  const { data: openInvoiceData, isLoading: isLoadingOpenInvoice } = useSwr<
    IChartDataResponse[]
  >(
    '/service-proxy/dashboard/grafico/financeiro/fatura_aberta/historico/produto',
    requestParamsOpened,
  );

  const formatCurrency = useCallback(
    (value: number): string => {
      const currencyOptions: Record<
        string,
        { locale: string; currency: string }
      > = {
        'pt-BR': { locale: 'pt-BR', currency: 'BRL' },
        en: { locale: 'en-US', currency: 'USD' },
        es: { locale: 'es-ES', currency: 'EUR' },
      };

      const { locale, currency } =
        currencyOptions[currentLanguage] || currencyOptions['pt-BR'];

      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      }).format(value);
    },
    [currentLanguage],
  );

  const formatAxisY = useCallback((value: string) => {
    const million = 1000000;
    const thousand = 1000;
    const formattedValue = Number(value);
    if (formattedValue >= million) {
      return `${(formattedValue / million).toFixed(1)}M`;
    }
    if (formattedValue >= thousand) {
      return `${(formattedValue / thousand).toFixed(1)}K`;
    }
    return formattedValue;
  }, []);

  const closedChart = useBillingChart({
    data: closedInvoiceData,
    isLoading: isLoadingClosedInvoice,
    invoiceType: 'closed',
    dateFormat: 'MM/YYYY',
    theme,
    graphThemeKey,
    formatCurrency,
  });

  const openedChart = useBillingChart({
    data: openInvoiceData,
    isLoading: isLoadingOpenInvoice,
    invoiceType: 'open',
    dateFormat: 'DD/MM',
    theme,
    graphThemeKey,
    formatCurrency,
  });

  const optionsClosed = useMemo(
    () => [
      { label: t('last-month', { days: 30 }), value: '30d' },
      { label: t('last-two-months', { days: 60 }), value: '60d' },
      { label: t('last-three-months', { days: 90 }), value: '90d' },
      { label: t('last-six-months', { days: 180 }), value: '180d' },
      { label: t('last-year', { days: 365 }), value: '365d' },
    ],
    [t],
  );

  const optionsOpened = useMemo(
    () => [
      { label: t('last-week', { days: 7 }), value: '7d' },
      { label: t('last-two-weeks', { days: 14 }), value: '14d' },
      { label: t('last-three-weeks', { days: 21 }), value: '21d' },
      { label: t('last-month', { days: 30 }), value: '30d' },
    ],
    [t],
  );

  const getValidOptionValue = useCallback(
    (
      searchParam: string,
      defaultValue: string,
      options: { value: string }[],
    ) => {
      const isValidValue = options.find(option => option.value === searchParam);

      if (!isValidValue && searchParam) {
        clearSearchParams();
        return defaultValue;
      }

      return isValidValue ? searchParam : defaultValue;
    },
    [clearSearchParams],
  );

  const filtersClosed = useMemo(
    () => [
      {
        visible: true,
        col: { xs: 24, sm: 12, md: 8, lg: 6, xl: 5 },
        label: t('consumer-traffic-by-customer:labelPeriod'),
        element: (
          <Select
            onChange={(lastTimeClosed: string) => {
              setFormData(state => ({ ...state, lastTimeClosed }));
              setSearchParams({ lastTimeClosed });
            }}
            value={getValidOptionValue(
              searchParams?.lastTimeClosed,
              formData?.lastTimeClosed,
              optionsClosed,
            )}
            options={optionsClosed}
            style={styles.select}
          />
        ),
      },
    ],
    [
      t,
      getValidOptionValue,
      searchParams?.lastTimeClosed,
      formData?.lastTimeClosed,
      optionsClosed,
      setSearchParams,
    ],
  );

  const filtersOpened = useMemo(
    () => [
      {
        visible: true,
        col: { xs: 24, sm: 12, md: 8, lg: 6, xl: 5 },
        label: t('consumer-traffic-by-customer:labelPeriod'),
        element: (
          <Select
            onChange={(lastTimeOpened: string) => {
              setFormData(state => ({ ...state, lastTimeOpened }));
              setSearchParams({ lastTimeOpened });
            }}
            value={getValidOptionValue(
              searchParams?.lastTimeOpened,
              formData?.lastTimeOpened,
              optionsOpened,
            )}
            options={optionsOpened}
            style={styles.select}
          />
        ),
      },
    ],
    [
      t,
      getValidOptionValue,
      searchParams?.lastTimeOpened,
      formData?.lastTimeOpened,
      optionsOpened,
      setSearchParams,
    ],
  );

  const isLoading = isLoadingClosedInvoice || isLoadingOpenInvoice;

  return (
    <>
      <BillingChartCard
        title={t('monthly-billing:title-invoice-closed')}
        tooltipText={t('monthly-billing:invoice-closed-tooltip')}
        isLoading={isLoading}
        hasData={closedChart.hasData}
        showFilters={showFiltersClosed}
        onToggleFilters={() => setShowFiltersClosed(prev => !prev)}
        filtersElement={filtersClosed}
        filteredChartData={closedChart.filteredChartData}
        groupedCategories={closedChart.groupedCategories}
        visibleCategories={closedChart.visibleCategories}
        chartAnnotations={closedChart.chartAnnotations}
        getChartColor={closedChart.getChartColor}
        getCategoryColor={closedChart.getCategoryColor}
        handleCategoryToggle={closedChart.handleCategoryToggle}
        handleCategoryIsolate={closedChart.handleCategoryIsolate}
        formatCurrency={formatCurrency}
        formatAxisY={formatAxisY}
        theme={theme}
      />

      <BillingChartCard
        title={t('monthly-billing:title-invoice-opened')}
        tooltipText={t('monthly-billing:invoice-opened-tooltip')}
        isLoading={isLoading}
        hasData={openedChart.hasData}
        showFilters={showFiltersOpened}
        onToggleFilters={() => setShowFiltersOpened(prev => !prev)}
        filtersElement={filtersOpened}
        filteredChartData={openedChart.filteredChartData}
        groupedCategories={openedChart.groupedCategories}
        visibleCategories={openedChart.visibleCategories}
        chartAnnotations={openedChart.chartAnnotations}
        getChartColor={openedChart.getChartColor}
        getCategoryColor={openedChart.getCategoryColor}
        handleCategoryToggle={openedChart.handleCategoryToggle}
        handleCategoryIsolate={openedChart.handleCategoryIsolate}
        formatCurrency={formatCurrency}
        formatAxisY={formatAxisY}
        theme={theme}
      />
    </>
  );
};

export default BillingDashboard;
